/**
 * LPP-22292 / SCFE-1 — Motivazione strutturata di mancata firma (ramo Network check-in).
 *
 * Logica pura (framework-agnostica) per:
 *  - enum esclusivo delle 3 motivazioni (VB-06);
 *  - mappatura motivazione -> code del testo di default che popola il campo firma;
 *  - risoluzione del testo di default tradotto per lingua (IT/FR/ES/PT) con fallback sul code grezzo.
 *
 * Le traduzioni dei code vivono sul FE (namespace i18n `network`, chiave
 * `raCheckOutIn.signature.missingSignature.defaultText.<CODE>`). Il backend NTW
 * (Task 3) valorizza il campo firma con il code grezzo; il FE lo traduce e lo
 * riporta sulla copia di check-in (Requirement 3.1).
 */

/** Enum motivazione mancata firma — owner del dominio, coerente con NTW/OPR (`signatureBypassReason`). */
export enum MissingSignatureReason {
	Keybox = "KEYBOX",
	CustomerRefused = "CUSTOMER_REFUSED",
	CustomerNotWaiting = "CUSTOMER_NOT_WAITING",
}

/** Elenco ordinato delle motivazioni selezionabili (ordine di presentazione UI). */
export const MISSING_SIGNATURE_REASONS: MissingSignatureReason[] = [
	MissingSignatureReason.Keybox,
	MissingSignatureReason.CustomerRefused,
	MissingSignatureReason.CustomerNotWaiting,
];

/**
 * Code del testo di default (chiave di traduzione) associato a ciascuna motivazione.
 * Sono i 3 code grezzi valorizzati da NTW (Task 3) e tradotti sul FE.
 */
export const DEFAULT_TEXT_CODE_BY_REASON: Record<MissingSignatureReason, string> = {
	[MissingSignatureReason.Keybox]: "MISSING_SIGNATURE_KEYBOX",
	[MissingSignatureReason.CustomerRefused]: "MISSING_SIGNATURE_CUSTOMER_REFUSED",
	[MissingSignatureReason.CustomerNotWaiting]: "MISSING_SIGNATURE_CUSTOMER_NOT_WAITING",
};

/** Type guard: verifica che un valore arbitrario sia una motivazione valida. */
export const isMissingSignatureReason = (value: unknown): value is MissingSignatureReason =>
	typeof value === "string" &&
	(MISSING_SIGNATURE_REASONS as string[]).includes(value);

/**
 * Ritorna il code del testo di default per la motivazione, o `null` se la
 * motivazione non è valida.
 */
export const getDefaultTextCode = (
	reason: MissingSignatureReason | null | undefined
): string | null => {
	if (!isMissingSignatureReason(reason)) {
		return null;
	}
	return DEFAULT_TEXT_CODE_BY_REASON[reason];
};

/**
 * Applica la selezione esclusiva (VB-06): selezionare una motivazione sostituisce
 * qualunque selezione precedente; ri-selezionare la motivazione già attiva la
 * deseleziona (toggle). Ritorna sempre al più UNA motivazione.
 */
export const applyExclusiveSelection = (
	current: MissingSignatureReason | null,
	next: MissingSignatureReason
): MissingSignatureReason | null => {
	if (!isMissingSignatureReason(next)) {
		return current;
	}
	return current === next ? null : next;
};

/**
 * Valida l'esclusività di una selezione multipla (VB-06): è ammessa al più UNA
 * motivazione. Una lista vuota è valida (nessuna motivazione = flusso firma normale);
 * più di una motivazione è invalida e deve bloccare la finalizzazione.
 */
export const isExclusiveSelectionValid = (
	selected: ReadonlyArray<MissingSignatureReason>
): boolean => selected.length <= 1;

/** Funzione di lookup della traduzione di un code (ritorna `null`/vuoto se assente). */
export type TranslationLookup = (code: string) => string | null | undefined;

/**
 * Risolve il testo di default che popola il campo firma per la motivazione data,
 * traducendo il relativo code tramite `lookup`. Se la traduzione è assente o vuota,
 * fa fallback sul **code grezzo** (Error Handling del design).
 */
export const resolveDefaultSignatureText = (
	reason: MissingSignatureReason | null | undefined,
	lookup: TranslationLookup
): string | null => {
	const code = getDefaultTextCode(reason);
	if (code === null) {
		return null;
	}
	const translated = lookup(code);
	if (typeof translated === "string" && translated.trim() !== "") {
		return translated;
	}
	return code; // fallback sul code grezzo
};
